import {
  AlertColor,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from '@mui/material';
import dayjs from 'dayjs';
import React, { FC, MouseEvent, RefObject, useRef, useState } from 'react';
import strings from '../../../../../../common/Translation/Translate';
import Form, { FormDataModel } from '../../../../../../components/Form';
import Notification from '../../../../../../components/Notification';
import { removeErrorFieldsFromValues } from '../../../../../../utils/validators';
import { UnavailabilityDateRange } from './unavailabilityDateUtils';
import { unavailabilityDateForm } from './unavailabilityDateForm';

type AddUnavailabilityDateModalProps = {
  open: boolean;
  isEdit: boolean;
  initialValues: Record<string, unknown>;
  onClose: () => void;
  onSave: (range: UnavailabilityDateRange) => void;
};

export const AddUnavailabilityDateModal: FC<AddUnavailabilityDateModalProps> = ({
  open,
  isEdit,
  initialValues,
  onClose,
  onSave,
}) => {
  const formRef: RefObject<Form | null | undefined> = useRef();
  const [hasError, setHasError] = useState(false);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const handleSave = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { getFormData } = formRef.current as {
      getFormData: () => { formData: FormDataModel; isFormValid: boolean };
    };
    const { formData, isFormValid } = getFormData();
    const data = removeErrorFieldsFromValues(formData);
    setHasError(false);

    if (!isFormValid) {
      setHasError(true);
      return;
    }

    if (
      dayjs(data.unavailabilityEndDate as string).isBefore(
        dayjs(data.unavailabilityStartDate as string),
      )
    ) {
      setMessage({
        display: true,
        severity: 'warning',
        message: strings.common_endDate_error_message,
      });
      return;
    }

    onSave({
      unavailabilityStartDate: dayjs(data.unavailabilityStartDate as string).format('DD-MM-YYYY'),
      unavailabilityEndDate: dayjs(data.unavailabilityEndDate as string).format('DD-MM-YYYY'),
    });
  };

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>{isEdit ? strings.unavailable_date_edit : strings.unavailable_date}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={1.5}>
              <Form
                key={open ? JSON.stringify(initialValues) : 'closed'}
                hasError={hasError}
                ref={formRef as RefObject<Form>}
                model={unavailabilityDateForm(strings)}
                values={initialValues}
              />
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} sx={{ textTransform: 'capitalize' }}>
            {strings.cancelText}
          </Button>
          <Button onClick={handleSave} variant="contained" sx={{ textTransform: 'capitalize' }}>
            {strings.common_button_save}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
